/**
 * MobileAuditRecordsList — audit cards ( <lg ). Batch 6.3.
 * GET /audits-qa/records — same filter contract as desktop.
 * All rendered fields (score_num, status, cqc_key_questions, open_findings,
 * findings_list.length) are server-authoritative. Whole card tap opens the
 * shared AuditFullDrawer for the same audit id desktop opens.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ChevronRight, Clock, CheckCircle2, AlertTriangle, AlertOctagon, ClipboardList } from "lucide-react";
import { MobileFilterDrawer } from "@/components/mobile";

const STATUS_META = {
  scheduled:       { label: "Scheduled",       Icon: Clock,          cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress:     { label: "In Progress",     Icon: Clock,          cls: "bg-sky-100 text-sky-800 border-sky-300" },
  action_required: { label: "Action Required", Icon: AlertTriangle,  cls: "bg-amber-100 text-amber-900 border-amber-300" },
  completed:       { label: "Completed",       Icon: CheckCircle2,   cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  overdue:         { label: "Overdue",         Icon: AlertOctagon,   cls: "bg-red-100 text-red-800 border-red-300" },
};

function StatusPill({ status, testid }) {
  const s = STATUS_META[status] || STATUS_META.scheduled;
  const Icon = s.Icon;
  return (
    <span data-testid={testid} data-status={status}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function MobileAuditRecordsList({ search, setSearch, onOpenAudit, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: "", category: "", status: "", cqc_key_question: "" });
  const [constants, setConstants] = useState({ categories: [], statuses: [], cqc_key_questions: [] });
  const [sites, setSites] = useState([]);
  const [localSearch, setLocalSearch] = useState(search || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (localSearch?.trim()) params.search = localSearch.trim();
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get("/audits-qa/records", { params });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters, localSearch]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => {
    api.get("/audits-qa/constants").then((r) => setConstants(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, []);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const siteName = (id) => sites.find((s) => s.id === id)?.name;

  return (
    <div className="space-y-3" data-testid="mobile-audits-records">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={localSearch}
            onChange={(e) => { setLocalSearch(e.target.value); setSearch?.(e.target.value); }}
            placeholder="Search audits…"
            data-testid="mobile-audits-search"
            className="w-full h-11 pl-8 pr-3 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>
        <MobileFilterDrawer
          triggerLabel="Filters"
          triggerClassName="shrink-0"
          activeCount={activeFilterCount}
          onClear={() => setFilters({ site_id: "", category: "", status: "", cqc_key_question: "" })}
          testid="mobile-audits-filter-drawer"
        >
          <div className="space-y-3">
            <FS label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })}
              options={sites.map((s) => ({ v: s.id, label: s.name }))} testid="mobile-filter-audits-site" />
            <FS label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })}
              options={(constants.categories || []).map((c) => ({ v: c.key, label: c.label }))} testid="mobile-filter-audits-category" />
            <FS label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
              options={(constants.statuses || []).map((s) => ({ v: s, label: s.replace(/_/g, " ") }))} testid="mobile-filter-audits-status" />
            <FS label="CQC Key Question" value={filters.cqc_key_question} onChange={(v) => setFilters({ ...filters, cqc_key_question: v })}
              options={(constants.cqc_key_questions || []).map((k) => ({ v: k, label: k }))} testid="mobile-filter-audits-cqc" />
          </div>
        </MobileFilterDrawer>
      </div>

      <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-audits-count">
        {rows.length} audit{rows.length === 1 ? "" : "s"}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-4" data-testid="mobile-audits-loading">Loading audits…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-audits-empty">
          <div className="text-sm font-semibold text-slate-700">No audits match the current filters.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mobile-audits-list">
          {rows.map((r) => {
            const cqc = r.cqc_key_questions || [];
            const findingsCount = (r.findings_list || []).length;
            return (
              <li key={r.id} data-testid={`mobile-audit-row-${r.id}`} className="rounded-lg border border-slate-200 bg-white">
                <button
                  type="button"
                  onClick={() => onOpenAudit?.(r.id)}
                  data-testid={`mobile-audit-card-${r.id}`}
                  className="w-full text-left p-3 min-h-[44px] active:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-lg"
                >
                  {/* Row 1 — Title + chevron */}
                  <div className="flex items-start gap-2">
                    <div className="font-semibold text-slate-900 flex-1 truncate" data-testid={`mobile-audit-title-${r.id}`}>
                      {r.title || "—"}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  </div>

                  {/* Row 2 — Category · Site */}
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    <span data-testid={`mobile-audit-category-${r.id}`}>{r.category_label || "—"}</span>
                    <span className="mx-1">·</span>
                    <span data-testid={`mobile-audit-site-${r.id}`}>{siteName(r.site_id) || "—"}</span>
                  </div>

                  {/* Row 3 — Conducted date · Auditor */}
                  <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="tabular-nums" data-testid={`mobile-audit-date-${r.id}`}>{r.conducted_date || "—"}</span>
                    <span className="text-slate-400">·</span>
                    <span data-testid={`mobile-audit-auditor-${r.id}`}>{r.auditor || "—"}</span>
                  </div>

                  {/* Row 4 — Score + Status pill (server-authoritative) */}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200 tabular-nums"
                      data-testid={`mobile-audit-score-${r.id}`}
                    >
                      Score {r.score_num != null ? `${r.score_num}%` : "—"}
                    </span>
                    <StatusPill status={r.status} testid={`mobile-audit-status-${r.id}`} />
                  </div>

                  {/* Row 5 — CQC pills (exact copy of server payload) */}
                  {cqc.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`mobile-audit-cqc-${r.id}`}>
                      {cqc.map((k) => (
                        <span key={k} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">
                          {k}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Row 6 — Findings summary (server counts) */}
                  {findingsCount > 0 && (
                    <div className="mt-1.5 text-[11px] text-slate-600 flex items-center gap-1" data-testid={`mobile-audit-findings-${r.id}`}>
                      <ClipboardList className="w-3 h-3 text-slate-500" />
                      <span className="tabular-nums">{r.open_findings ?? 0} open · {findingsCount} total</span>
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FS({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
        <option value="">All</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
